import type { CapsuleView } from "./capsules";

export type MessageTab = "unlocked" | "locked" | "watching" | "following" | "archived" | "blocked";
/** Filters the server applies (PLAN.md 4.20): sorts by opening or receive time only (D2, D3). */
export type MessageFilters = { person: string; group: string; receivedFrom: string; receivedTo: string; opensFrom: string; opensTo: string; sort: "received" | "opens"; direction: "asc" | "desc" };
/** Lists of capsules still to open (Still locked, Watching) start with the one that opens next; the others with the latest. */
export const defaultFilters = (tab?: MessageTab): MessageFilters => ({ person: "", group: "", receivedFrom: "", receivedTo: "", opensFrom: "", opensTo: "", sort: "opens", direction: tab === "locked" || tab === "watching" ? "asc" : "desc" });
export const normalizeSearch = (value: string) => value.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase().trim();
export const creationTime = (view: CapsuleView) => (view.createdKnown ?? !view.isPrivate) ? view.createdAt * 1000 : null;
/** datetime-local is in the browser's zone; "from" starts the minute, "to" includes all of it. Unix seconds. */
export function rangeSeconds(value: string, end: boolean): number | undefined {
  if (!value) return undefined;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? undefined : Math.floor((ms + (end ? 59_999 : 0)) / 1000);
}
/** The server takes at most this many member ids in `members=`. */
export const MAX_FILTER_MEMBERS = 50;
export type ListQuery = { path: string; params: Record<string, string> };
/**
 * The server request for a tab and its filters (PLAN.md 4.20). Person, group and the people panel
 * each narrow to member ids; together they keep only the members in all of them. null: no member
 * can match, so there is nothing to ask for.
 */
export function listQuery(input: { source: "feed" | "inbox" | "sent"; tab: MessageTab; filters: MessageFilters; memberSets: (number[] | null)[]; read?: boolean }): ListQuery | null {
  const { source, tab, filters } = input;
  const params: Record<string, string> = { limit: "50", sort: filters.sort, dir: filters.direction };
  if (input.read !== undefined) params.read = input.read ? "1" : "0";
  let path = `/api/${source}`;
  if (tab === "watching") path = "/api/watching";
  if (tab === "unlocked" || tab === "locked") params.locked = tab === "locked" ? "1" : "0";
  if (tab === "archived") params.archive = "1";
  if (tab === "blocked") { params.blocked = "1"; params.archive = "all"; }
  else if (source === "inbox") params.blocked = "0";
  if (tab === "following") params.following = "1";
  const sets = input.memberSets.filter((set): set is number[] => set !== null);
  if (sets.length) {
    const ids = sets.reduce((kept, set) => kept.filter((id) => set.includes(id)));
    if (ids.length === 0) return null;
    params.members = [...new Set(ids)].sort((a, b) => a - b).slice(0, MAX_FILTER_MEMBERS).join(",");
  }
  const bounds: [string, string, boolean][] = [["received_from", filters.receivedFrom, false], ["received_to", filters.receivedTo, true], ["opens_from", filters.opensFrom, false], ["opens_to", filters.opensTo, true]];
  for (const [name, value, end] of bounds) { const at = rangeSeconds(value, end); if (at !== undefined) params[name] = String(at); }
  return { path, params };
}
/** The Correspondence tabs (PLAN.md 4.21 D11): what I asked for, what I was asked for, and the archived ones of both. */
export type EnvelopeTab = "sent" | "received" | "archived";
/**
 * The server request for one Correspondence list: the same filters as Sent and Inbox, no
 * unlocked/locked split (a request's replies each have their own time).
 */
export function envelopeQuery(input: { source: "envelopes_sent" | "envelopes_received"; archived: boolean; filters: MessageFilters; memberSets: (number[] | null)[] }): ListQuery | null {
  const { source, archived, filters } = input;
  const params: Record<string, string> = { limit: "50", sort: filters.sort, dir: filters.direction };
  if (archived) params.archive = "1";
  else if (source === "envelopes_received") params.blocked = "0";
  const sets = input.memberSets.filter((set): set is number[] => set !== null);
  if (sets.length) {
    const ids = sets.reduce((kept, set) => kept.filter((id) => set.includes(id)));
    if (ids.length === 0) return null;
    params.members = [...new Set(ids)].sort((a, b) => a - b).slice(0, MAX_FILTER_MEMBERS).join(",");
  }
  const bounds: [string, string, boolean][] = [["received_from", filters.receivedFrom, false], ["received_to", filters.receivedTo, true], ["opens_from", filters.opensFrom, false], ["opens_to", filters.opensTo, true]];
  for (const [name, value, end] of bounds) { const at = rangeSeconds(value, end); if (at !== undefined) params[name] = String(at); }
  return { path: source === "envelopes_sent" ? "/api/correspondence/sent" : "/api/correspondence/received", params };
}
/** Refresh: a fresh row replaces an old one unless the old one holds content opened or revealed under the same Lock count. */
export function keepOpened(previous: CapsuleView[], fresh: CapsuleView[], locks: number): CapsuleView[] {
  const byId = new Map(previous.map((view) => [view.id, view]));
  return fresh.map((view) => {
    const old = byId.get(view.id);
    if (!old || old.gen !== locks) return view;
    return (old.opened !== null && view.opened === null) || (old.fromKey && !view.fromKey) ? old : view;
  });
}
